import { useMemo, useState } from "react";
import { Link } from "react-router";
import type { GitGraphCommit } from "@roomate/core";
import { Avatar } from "../calm.tsx";
import { cx, formatAgo } from "../ui.tsx";

/*
 * A commit graph like VS Code's Git Graph: every line of work gets a coloured lane, merges curve
 * into main, branch tips carry a chip. Lane assignment is the usual one for a topologically sorted
 * log: each lane "expects" a commit; a commit takes the lane that expects it (or a new one), its
 * first parent inherits the lane and further parents open or join lanes.
 */

const ROW = 30;
const COL = 16;
const R = 4.5;
const COLORS = ["#4c8dff", "#2fae6a", "#e49b26", "#d16ba5", "#8b6cf0", "#26a6b8", "#e5604d", "#9aa53a"];

type Row = { c: GitGraphCommit; lane: number; edges: { from: number; to: number; color: number; kind: "pass" | "parent" }[]; color: number };

function layout(commits: GitGraphCommit[]): { rows: Row[]; width: number } {
  const lanes: (string | null)[] = []; // sha each lane is waiting for
  const laneColor: number[] = [];
  let nextColor = 0;
  const rows: Row[] = [];
  let maxLanes = 1;

  for (const c of commits) {
    let lane = lanes.indexOf(c.sha);
    if (lane === -1) {
      lane = lanes.indexOf(null);
      if (lane === -1) lane = lanes.length;
      lanes[lane] = c.sha;
      laneColor[lane] = nextColor++ % COLORS.length;
    }
    const color = laneColor[lane]!;
    // Other lanes also waiting for this commit converge into it (a branch point).
    const converging: number[] = [];
    lanes.forEach((s, i) => {
      if (s === c.sha && i !== lane) converging.push(i);
    });

    const edges: Row["edges"] = [];
    // Lanes that just pass through this row.
    lanes.forEach((s, i) => {
      if (s && i !== lane && !converging.includes(i)) edges.push({ from: i, to: i, color: laneColor[i]!, kind: "pass" });
    });
    for (const i of converging) {
      edges.push({ from: i, to: lane, color: laneColor[i]!, kind: "pass" });
      lanes[i] = null;
    }

    // Parents: the first keeps this lane; others reuse a lane already waiting for them or open one.
    const [first, ...rest] = c.parents;
    lanes[lane] = first ?? null;
    if (first) edges.push({ from: lane, to: lane, color, kind: "parent" });
    for (const p of rest) {
      let pl = lanes.indexOf(p);
      if (pl === -1) {
        pl = lanes.indexOf(null);
        if (pl === -1) pl = lanes.length;
        lanes[pl] = p;
        laneColor[pl] = nextColor++ % COLORS.length;
      }
      edges.push({ from: lane, to: pl, color: laneColor[pl]!, kind: "parent" });
    }
    while (lanes.length && lanes[lanes.length - 1] === null) lanes.pop();
    maxLanes = Math.max(maxLanes, lanes.length, lane + 1);
    rows.push({ c, lane, edges, color });
  }
  return { rows, width: Math.min(maxLanes, 12) * COL + 12 };
}

const x = (lane: number) => 10 + lane * COL;

export function GitGraph({ commits, taskByBranch }: { commits: GitGraphCommit[]; taskByBranch?: Map<string, string> }) {
  const [limit, setLimit] = useState(40);
  const shown = commits.slice(0, limit);
  const { rows, width } = useMemo(() => layout(shown), [shown]);
  if (commits.length === 0) return <p className="text-[13px] text-ink-3">No history yet.</p>;

  return (
    <div data-testid="git-graph">
      <div className="relative overflow-x-auto">
        <svg width={width} height={rows.length * ROW} className="absolute left-0 top-0" aria-hidden>
          {rows.map((r, i) => {
            const y = i * ROW + ROW / 2;
            return (
              <g key={r.c.sha}>
                {r.edges.map((e, j) => {
                  const x1 = x(e.from);
                  const x2 = x(e.to);
                  const stroke = COLORS[e.color]!;
                  if (e.kind === "pass") {
                    // Through the row (top to bottom), bending if the lane moves.
                    const top = i * ROW;
                    const bottom = top + ROW;
                    return x1 === x2 ? (
                      <line key={j} x1={x1} y1={top} x2={x1} y2={bottom} stroke={stroke} strokeWidth={2} strokeOpacity={0.85} />
                    ) : (
                      <path key={j} d={`M${x1} ${top} C ${x1} ${y}, ${x2} ${y}, ${x2} ${bottom}`} fill="none" stroke={stroke} strokeWidth={2} strokeOpacity={0.85} />
                    );
                  }
                  // From the commit down to its parent's lane.
                  const bottom = (i + 1) * ROW;
                  return x1 === x2 ? (
                    <line key={j} x1={x1} y1={y} x2={x2} y2={bottom} stroke={stroke} strokeWidth={2} strokeOpacity={0.85} />
                  ) : (
                    <path key={j} d={`M${x1} ${y} C ${x1} ${bottom - 4}, ${x2} ${y + 4}, ${x2} ${bottom}`} fill="none" stroke={stroke} strokeWidth={2} strokeOpacity={0.85} />
                  );
                })}
                <circle cx={x(r.lane)} cy={y} r={r.c.parents.length > 1 ? R + 1 : R} fill={r.c.parents.length > 1 ? "var(--paper)" : COLORS[r.color]} stroke={COLORS[r.color]} strokeWidth={2} />
              </g>
            );
          })}
        </svg>
        <ol style={{ paddingLeft: width + 8 }}>
          {rows.map((r) => (
            <li key={r.c.sha} className="flex min-w-0 items-center gap-2.5" style={{ height: ROW }}>
              {r.c.refs.map((ref) => {
                const task = taskByBranch?.get(ref);
                const chip = (
                  <span className={cx("shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium text-white")} style={{ background: COLORS[r.color] }}>
                    {ref.replace(/^rm\//, "").replace(/^(\d+)-.*/, "#$1")}
                  </span>
                );
                return task ? (
                  <Link key={ref} to={`/tasks/${task}`} title={ref}>
                    {chip}
                  </Link>
                ) : (
                  <span key={ref} title={ref}>
                    {chip}
                  </span>
                );
              })}
              <span className={cx("min-w-0 flex-1 truncate text-[13px]", r.c.parents.length > 1 ? "text-ink-2" : "text-ink")} title={r.c.subject}>
                {r.c.subject}
              </span>
              <Avatar name={r.c.author} kind={/agent|claude|bot/i.test(r.c.author) ? "agent" : "human"} size={18} />
              <span className="w-14 shrink-0 text-right text-[11.5px] tabular-nums text-ink-3">{formatAgo(Date.now() - r.c.at)}</span>
              <span className="hidden w-14 shrink-0 font-mono text-[11px] text-ink-3 md:inline">{r.c.sha.slice(0, 7)}</span>
            </li>
          ))}
        </ol>
      </div>
      {commits.length > limit && (
        <button onClick={() => setLimit((l) => l + 60)} className="mt-3 text-[13px] text-ink-2 hover:text-ink">
          Show older commits
        </button>
      )}
    </div>
  );
}
